import { Fragment } from "react";
import { getBuckeyeLinkData, getGoogle, getPerson } from "../../services/data-sources/enums";
import { searchOptions } from "../../services/graphql/constants";
import { selectBuckeyeLinkQuery, selectCategorizedResults } from "../../services/graphql/slices/buckeye-link";
import { RESOURCE_TYPES } from "../../util/constants";
import { alterTitleToId, sortByStringField } from "../../util/functions";
import { categories } from "./categories";
import { Mui, MuiIcons } from "@osu/react-ui";
import { determinePagination } from "../../services/graphql/buildQueries";

const sortFiltersByAlphabet = (filters = []) => {
  return filters.sort((a, b) => (a?.title > b?.title ? 0 : -1))
}

export const filterBySubcategory = (subcat, items = []) => {
  const highlights = {}
  highlights.id = alterTitleToId(subcat);
  highlights.label = subcat;
  highlights.items = items.filter(({ category, subcategory }) => subcat && [category, subcategory].includes(subcat));

  if (!highlights?.items?.length) {
    highlights.items = items.filter(({ tags = [] }) => subcat && tags?.length && tags.includes(subcat));
  }

  return highlights
}

function createFeaturedLinks(featured = {}, items = []) {
  let featuredResults = {};
  if (featured) {
    Object.values(RESOURCE_TYPES).forEach((type) => {
      if (featured?.[type]?.length) {
        featuredResults[type] = [];
        const subcategories = featured[type];
        subcategories.forEach((subcat) => {
          let highlights = filterBySubcategory(subcat, items)
          if (subcat) {
            if (highlights?.items?.length) {
              highlights.items = highlights.items.slice(0, 3);
              featuredResults[type].push(highlights);
            }
          }
        });
      }
    });
  }

  return featuredResults;
}

function _createCategorizedResultsAndFilters(categories, includeFilters) {
  let categorizedResults = [],
    filters = [];
  categories.forEach((cat) => {
    const id = alterTitleToId(cat);
    categorizedResults.push({
      id,
      title: cat,
      query: getBuckeyeLinkData,
      result: {
        items: [],
      },
    });
    if (includeFilters) {
      filters.push({
        id,
        title: cat,
      });
    }
  });

  const sortedFilters = sortFiltersByAlphabet(filters)
  return {
    categories: categorizedResults,
    filters: sortedFilters,
  };
}

export const filterItemsByRole = (items = [], affiliationFilters = []) => {
  const affiliationsToFilterOn = (affiliationFilters ?? []).filter(({ checked }) => !!checked).map(({ name }) => name)
  let filteredItems = items
  if(affiliationsToFilterOn?.length) {
    filteredItems = filteredItems.filter(({ roles }) => roles?.length && roles.some(role => affiliationsToFilterOn.includes(role)))
  } 

  return filteredItems
}

export function createBuckeyeLinkSearchResults(buckeyelink, options = {}) {
  const { includeFilters = false, categories: overrideCategories = [], query } = options;
  const items = buckeyelink?.items;
  let categories = overrideCategories,
    categorizedResults = [],
    filters = [],
    featuredResults = {};

  if (items?.length) {
    if (!categories.length) {
      categories = [...new Set(items.map(({ category }) => category))];
    }

    const { categories: cats = [], filters: fils = [] } = _createCategorizedResultsAndFilters(categories, includeFilters);
    filters = fils;
    categorizedResults = cats;

    let idx = 0
    items.forEach((item = {}) => {
      if (item?.category || item?.subcategory) {
        let correspondingCategory = categorizedResults.find(({ title }) => item.category === title);
        const uuid = (correspondingCategory?.id ?? "-no-category") + "-search-results-" + idx + (query ?? "-no-query")
        if (correspondingCategory?.result?.items) {
          correspondingCategory.result.items.push(item);
        }

        correspondingCategory.uuid = uuid
        correspondingCategory.totalResults = correspondingCategory?.result?.items?.length;
      }
      idx ++
    });
  }
  
  categorizedResults.forEach(({ result = {} }) => {
    if(result && !query) {
      result.items = sortByStringField(result.items ?? [], 'title')
    }
  })

  return {
    items,
    categories,
    categorizedResults,
    featuredResults,
    filters,
  };
}

export const selectResults = (state, active, { loading: isLoading, preview, query }) => {
  const results = selectCategorizedResults(state, {
    allowAffiliationFilter: !preview
  });
  const blQ = selectBuckeyeLinkQuery(state);

  let bl = [];
  if (active?.length) {
    const filteredResults = results.filter(({ id }) => {
      return active.includes(id);
    });
    bl = filteredResults;
  }
  const allowResults = preview ? query && query === blQ : true;
  if (allowResults) {
    bl = results.filter(({ result }) => {
      return result?.items?.length || isLoading;
    });
  }

  return bl
};

export const compareResultsAndQuery = (a, b) => {
  if(!a?.length && !b?.length) {
    return true
  }
  if(a.length !== b.length) {
    return false
  } 
  const checkIfLoading = (arr = []) => arr.some(el => el.isLoading)
  if(checkIfLoading(a) !== checkIfLoading(b)) {
    return false
  }
  const fullmatch = (a ?? []).every(({ key, id, query, sortPriority, title, totalResults, result = {} }, index) => {
    const correspondingBMatch = b[index]
    
    const toplevelMatch = {
      key,
      id,
      query,
      sortPriority,
      title,
      totalResults
    }
    const matching = Object.entries(toplevelMatch).every(([key, value]) => {
      return correspondingBMatch[key] === value
    })
    
    return matching
  })
  
  return fullmatch
};

let searchResultOptions = [];
Object.values(searchOptions).forEach((queryType) => {
  const match = categories.find(({ query }) => query === queryType);
  if (match) {
    searchResultOptions.push({
      ...match,
      result: {
        items: [],
      },
    });
  }
});

const defaultSections = searchResultOptions.filter(o => !(o?.roles?.length))

export const createGenericResults = ({ sections = defaultSections, query, loading, data, secondaryLoading }) => {
  if (!query && !secondaryLoading && !loading) {
    return [];
  }
  let searchRes = [],
    max = 3;

  sections.forEach(({ query: queryType, result, preview, ...restOfOption }, idx) => {
    const rest = {
      ...restOfOption,
      query: queryType,
      items: [],
    };
    const queries = data?.[queryType]?.queries;
    const showMoreGoogle = queryType === getGoogle && determinePagination(queries)
    const showMoreFindPeople = queryType === getPerson && data?.[queryType]?.nextPageLink
    if(showMoreGoogle || showMoreFindPeople) {
      rest.showMoreResults = true
    }
    if (idx >= max) {
      return false;
    }
    let clonedResult = { ...result, query: queryType };
    let isLoading = loading;
    clonedResult.items = [];
    const currentItems = data?.[queryType]?.items ?? [];
    if (currentItems?.length) {
      clonedResult.items = [...currentItems];
    }
    const uuid = queryType + (query ?? "-no-query") + idx
    if (secondaryLoading) {
      [...Array(10)].forEach((empty, idx) => {
        clonedResult.items.push({ isLoading: true, id: `loading-${idx}` + uuid });
      });
    }

    let totalResults = clonedResult?.items?.length;
    if(queryType === getGoogle) totalResults = queries?.nextPage?.[0]?.totalResults
    if(queryType === getPerson) totalResults = data?.[queryType]?.totalItems
    if (totalResults) {
      const showing = clonedResult?.items?.length ?? 0;
      clonedResult.subheading = `Showing ${showing} of ${totalResults} Results`;
    }


    searchRes.push({
      ...rest,
      isLoading,
      result: clonedResult,
      totalResults,
      uuid,
    });
  });

  return searchRes;
};

const SEARCH_LOADING_SKELETONS = {
  title: <Mui.Skeleton variant="text" sx={{ fontSize: "2rem", mr: "60%" }} />,
  snippet: (
    <Fragment>
      <Mui.Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Mui.Skeleton variant="text" sx={{ fontSize: "1rem", mr: "80%" }} />
    </Fragment>
  ),
};
const createSearchItems = ({ isLoading, result, preview }) => {
  let items = [];
  if (result?.isLoading ?? isLoading) {
    items.push(SEARCH_LOADING_SKELETONS);
  } else if (result?.items?.length) {
    items = result.items;
  } else if (preview) {
    items.push({
      title: "No results found.",
    });
  }
  return items;
};

const createInputProps = (preview) => {
  const endSentence = preview ? `view all search results` : `search immediately`
  return {
      helperText: <Fragment>
  <MuiIcons.InfoOutlined fontSize="inherit" sx={{ mr: .25, mb: .25  }} />
      Results will automatically update as you type. Press enter key or click the search magnifying glass to {endSentence}.
  </Fragment>,
  FormHelperTextProps: {
      className: `display-flex align-items-center`
  }
  }
}

export { searchResultOptions, createFeaturedLinks, createSearchItems, SEARCH_LOADING_SKELETONS, createInputProps };
